import React from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useNotifications } from '../../../contexts/notificationContext'
import { DAIRequest } from '../../../utils/DAIRequest'
import DAIPasswordInput from 'src/DAIComponents/Input/Password/DAIPasswordInput'
import { DAIForm } from 'src/DAIComponents/Form/DAIForm'

type password = {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}
// craete password validation schema
const passwordSchema = z
  .object({
    currentPassword: z.string().min(6),
    newPassword: z.string().min(6),
    confirmPassword: z.string().min(6),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export default function ChangePasswordSection() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<password>({
    resolver: zodResolver(passwordSchema),
  })
  const { addNotification } = useNotifications()

  const passwordMutation = useMutation({
    mutationFn: (data: password) => {
      return DAIRequest('POST', '/api/v1/users/changePassword', {
        body: data,
      })
    },
    onError: () => {
      addNotification('error', 'Password update failed')
    },
    onSuccess: () => {
      reset()
      addNotification('success', 'Password updated')
    },
  })

  const onSubmit = (data: password) => {
    passwordMutation.mutate(data)
  }

  return (
    <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
      <div>
        <h2 className="text-base font-semibold leading-7 text-gray-900">
          Change password
        </h2>
        <p className="mt-1 text-sm leading-6 text-gray-400">
          Update your password associated with your account.
        </p>
      </div>

      <DAIForm className="md:col-span-2" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
          <div className="col-span-full">
            <DAIPasswordInput
              label="Current password"
              name="current-password"
              error={errors.currentPassword?.message}
              required
              register={register('currentPassword')}
            />
          </div>

          <div className="col-span-full">
            <label
              htmlFor="new-password"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              New password
            </label>
            <div className="mt-2">
              <DAIPasswordInput
                name="new-password"
                showStrengthMeter={true}
                error={errors.newPassword?.message}
                required
                register={register('newPassword')}
              />
            </div>
          </div>
          <div className="col-span-full">
            <label
              htmlFor="confirm-password"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Confirm password
            </label>
            <div className="mt-2">
              <DAIPasswordInput
                name="confirm-password"
                error={errors.confirmPassword?.message}
                required
                register={register('confirmPassword')}
              />
            </div>
          </div>
        </div>

        <div className="mt-8 flex">
          <button
            type="submit"
            className="rounded-md bg-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
          >
            Save
          </button>
        </div>
      </DAIForm>
    </div>
  )
}
